import CommonLayout from 'layouts/DashboardLayout';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteStore, fetchStores } from '../../redux/slices/storeSlice';
import { Box, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { useNavigate } from 'react-router-dom';
import MDButton from 'components/MDButton';
import { formatDate } from 'utilities/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Search from 'examples/Search';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import StatusText from 'common/component/StatusText';
import { openSnackbar } from '../../redux/slices/common';
import { formatUsPhoneNumber } from 'utilities/common';
import MDBox from 'components/MDBox';
import { formatTimestampToWithoutTime } from 'utilities/common';
import FilterStores from 'common/CommonDrawer/stores/FilterStore';
import { formatAddress } from 'utilities/common';
import { STILO } from 'utilities/constants';
import { getData } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import { HEMP } from 'utilities/constants';

export default function Stores() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageRef = useRef(0);
  const searchTermRef = useRef('');
  const mainGridApiRef = useRef('');
  const gridRefApi = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const storesFiltersPayload = useRef({});
  const adminsMode = getData('adminsMode')
  const isStilo = JSON.parse(adminsMode) === STILO
  const isHemp = JSON.parse(adminsMode) === HEMP

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const { stores = {}, loading = false } = useSelector(state => state.stores);
  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "POS", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "POS", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "POS", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "POS", "Delete");


  // const { paidStoresCount = 0, unpaidStoresCount = 0, totalStores = 0 } = stores || {};

  const deleteHandler = (storeId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteStore(storeId)).unwrap().then((res) => {
      showError(res?.message, 'success');
      dispatch(closeCustomModalDialog());
      pageRef.current = 0;
      searchFn();
      getRows(gridRefApi.current);
    }).catch((err) => {
      dispatch(setModalLoader(false));
      showError(err?.message, 'error');
    });
  };

  const showError = (message, errorType) => {
    if (errorType === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errorType === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const applyStoreFilter = (filterPayload) => {
    pageRef.current = 0;
    storesFiltersPayload.current = filterPayload;
    const dataSource = {
      getRows: (params) => {
        dispatch(fetchStores({ skip: pageRef.current, searchTerm: searchTermRef.current, limit: 20, ...filterPayload?.queryPayload })).unwrap()
          .then(res => {
            const { stores, total } = res;
            const storesArray = stores?.map((item => {
              let valiDateFrom = item?.valiDateFrom ? new Date(item?.valiDateFrom) : null;
              let valiDateTo = item?.valiDateTo ? new Date(item?.valiDateTo) : null;
              let validity = '';
              if (valiDateFrom) {
                validity = `${formatTimestampToWithoutTime(valiDateFrom)} to ${valiDateTo ? formatTimestampToWithoutTime(valiDateTo) : '-'}`;
              } else {
                validity = 'Lifetime';
              }
              return {
                ...item, status: item?.status === 'pending' ? 'Pending' : item?.status === 'active' ? 'Active' : 'Inactive',
                createdDate: formatTimestampToWithoutTime(item?.createdDate),
                valiDateTo: valiDateTo ? formatDate(valiDateTo) : '-',
                validity: validity === 'Lifetime' ? 'Lifetime' : formatTimestampToWithoutTime(valiDateTo),
                address: item.address ? formatAddress(item?.address) : '-',
              };
            }));
            params.successCallback([...storesArray], total);
            pageRef.current = pageRef.current + 20;
          });
      }
    };
    if (mainGridApiRef?.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption('datasource', dataSource);
    }
  };

  const columnDefs = [
    {
      headerName: 'Store Id',
      field: 'storeId',
      key: 'storeId',
      suppressMenu: false,
      cellRenderer: (props) => {
        return <Typography variant='caption' display='block' fontWeight='regular' sx={{ cursor: 'pointer', whiteSpace: 'normal', textTransform: 'capitalize' }}>{props?.data?.storeId}</Typography>;
      },
      minWidth: 160,
    },
    {
      headerName: 'Store Name',
      field: 'storeName',
      suppressMenu: false,
      minWidth: 160
    },
    {
      headerName: 'Contact Name',
      field: 'contactName',
      suppressMenu: true,
      sortable:false,
      minWidth: 160,
    },
    {
      headerName: 'E-mail',
      field: 'email',
      suppressMenu: false,
      minWidth: 300,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (props) => {
        return (
          <MDBox textAlign='left' lineHeight={1.75} mt={.125}>
            <Typography variant='h6' fontSize={14} color='dark' lineHeight={1.25} fontWeight='semibold' className='truncate line-1'>{(props?.data?.email)}</Typography>
            <Typography variant='p' fontSize={12} color='secondary' lineHeight={1.25}>{props?.data?.phone ? formatUsPhoneNumber(props?.data?.phone) : '-'}</Typography>
          </MDBox>
        );
      }
    },
    // {
    //   headerName: 'Status',
    //   field: 'status',
    //   suppressMenu: false,
    //   cellRenderer: (props) => {
    //     if (props?.data?.status !== 'pending') {
    //       return <StatusText status={props?.data?.status} />;
    //     }
    //   },
    //   minWidth: 120
    // },
    {
      headerName: 'Address',
      field: 'address',
      sortable:false,
      suppressMenu: false,
      minWidth: 300,
      cellRenderer: (props) => {
        return <Tooltip title={props?.value} arrow placement='top'>
          <Typography className='truncate line-2' variant='caption' display='block' fontWeight='regular' sx={{ whiteSpace: 'normal' }}>{props?.value}</Typography>
        </Tooltip>;
      },
    },
    // ...(isHemp ? [{
    //   headerName: 'Subscription',
    //   field: 'subscription',
    //   suppressMenu: false,
    //   minWidth: 120,
    //   cellRenderer: (props) => {
    //     return <div className={`${props.value === 'Paid' ? 'paid-badge' : props.value === 'Suspended' ? 'suspended-badge' : 'unpaid-badge'}`}>{props.value}</div>;
    //   },
    // }] : []),
    // {
    //   headerName: 'Subscription',
    //   field: 'subscription',
    //   suppressMenu: false,
    //   minWidth: 120,
    //   cellRenderer: (props) => {
    //     return <div className={`${props.value === 'Paid' ? 'paid-badge' : props.value === 'Suspended' ? 'suspended-badge' : 'unpaid-badge'}`}>{props.value}</div>;
    //   },
    // },
    {
      headerName: 'Created Date',
      field: 'createdDate',
      suppressMenu: false,
      minWidth: 160
    },

    // {
    //   headerName: 'Valid Upto',
    //   field: 'validity',
    //   suppressMenu: false,
    //   minWidth: 160
    // },
    // Conditionally include "Valid Upto" only if NOT in Stilo mode
    // ...(isHemp ? [{
    //   headerName: 'Valid Upto',
    //   field: 'validity',
    //   suppressMenu: false,
    //   minWidth: 160
    // }] : []),
    {
      headerName: 'Action',
      suppressMenu: true,
      minWidth: 100,
      sortable:false,
      cellRenderer: (props) => {
        return <Box className='action-edit w-100 jc-end' display='flex'>
          <IconButton
            disabled={!canEdit}
            onClick={(e) => {
              e.stopPropagation();
              onEditClicked(props);
            }} color='primary' size='small' variant='primary'>
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton disabled={!canDelete} onClick={(e) => {
            e.stopPropagation();
            dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props.data?.storeId, deleteType: 'store' }, modal: 'DELETE_MODAL' }));
          }} color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}>
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </Box>;
      }
    }
  ];

  const getRows = async (params) => {
    gridRefApi.current = params;
    let filter = {};
    if (params?.sortModel.length) {
      filter = { ...getSortInfo(params?.sortModel) };
    }
    dispatch(fetchStores({ skip: pageRef.current, searchTerm: searchTermRef.current, limit: 20, ...filter })).unwrap().then(res => {
      const { stores, total } = res;
      const today = new Date();
      const storesArray = stores?.map((item => {
        let valiDateFrom = item?.valiDateFrom ? new Date(item?.valiDateFrom) : null;
        let valiDateTo = item?.valiDateTo ? new Date(item?.valiDateTo) : null;
        let isSuspended = valiDateTo && valiDateTo < today;
        let validity = '';
        if (valiDateFrom) {
          validity = `${formatTimestampToWithoutTime(valiDateFrom)} to ${valiDateTo ? formatTimestampToWithoutTime(valiDateTo) : '-'}`;
        } else {
          validity = 'Lifetime';
        }
        return {
          ...item, status: item?.status === 'pending' ? 'Pending' : item?.status === 'active' ? 'Active' : 'Inactive',
          createdDate: formatTimestampToWithoutTime(item?.createdDate),
          valiDateFrom: valiDateFrom ? formatDate(valiDateFrom) : '-',
          valiDateTo: valiDateTo ? formatDate(valiDateTo) : '-',
          validity: validity === 'Lifetime' ? 'Lifetime' : formatTimestampToWithoutTime(valiDateTo),
          address: item.address ? formatAddress(item?.address) : '-',
          subscription: isSuspended ? 'Suspended' : (item.subscription)
        };
      }));
      params.successCallback([...storesArray], total);
      pageRef.current = pageRef.current + 20;
    }).catch(() => {
      params.successCallback([], 0);
    });
  };
  function getSortInfo(data) {
    console.log("data", data);

    const sortInfo = {
      sortField: "",
      sortOrder: "",
    };
    data.forEach((item) => {
      sortInfo.sortField = item.colId;
      sortInfo.sortOrder = item.sort === "asc" ? "asc" : "desc";
    });
    return sortInfo;
  }

  const searchFn = () => {
    const dataSource = {
      getRows
    };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption('datasource', dataSource);
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName !== 'Action') {
      navigate(`/stores/detail/${row?.data.storeId}?filter=storeDetails`);
    }
  };

  const onEditClicked = (row) => {
    navigate(`/stores/${row?.data.storeId}`);
  };

  const searchHandler = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
    searchTermRef.current = value;
    handleSearch(value);
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (stores?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      }
      else {
        mainGridApiRef.current.hideOverlay();
      }
    }

  }, [stores, loading]);

  return (<>
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }}>
        <Grid container alignItems='center' rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4'>Stores</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack direction='row' justifyContent={{ xs: 'start', sm: 'end' }} flexWrap='wrap' alignItems='stretch' gap={2}>
              <Search placeholder='Search' label='' value={searchTerm} onChange={searchHandler} />
              {
                canCreate &&
                <MDButton
                  startIcon={<AddRoundedIcon fontSize='large' />}
                  variant='contained' color='primary'
                  onClick={() => navigate('/stores/add')} >
                  Add Store
                </MDButton>
              }
            </Stack>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={12}>
        </Grid>
        <FilterStores applyStoreFilter={applyStoreFilter} />
        {
          canView &&
          <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} onRowClicked={onRowClicked} style={{ height: '58vh' }} />
        }
      </Box>
    </CommonLayout>
  </>);

}